import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { throttle, pick } from 'lodash';
import md5 from 'md5';
import { useAcl, useApiFieldErrors, useI18n, usePlanLimits, useReportForm, useReportUtils, useTutorialIntro, useTutorialApi } from '@/composables';
import { $emitter } from '@/services/emitter';
import helpers from '@/services/helpers';
import availableTokens from '@/views/reports/forms/landing/config/availableTokens';
import {
  getLandingApiRequest,
  postCreateLandingApiRequest,
  putUpdateLandingApiRequest,
} from '@/services/api/landings-api';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { useStore } from '@/store';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';

export type LandingDialogMode = 'create' | 'edit' | 'duplicate';

export type LandingDialogData = {
  mode?: LandingDialogMode;
  id?: string;
  workspaceId?: string;
  onClose?: (result: unknown) => void;
  onEdit?: (data: unknown) => void;
};

type LandingForm = {
  status: string;
  countryId: number;
  url: string;
  offers: number;
  name?: string;
  tags?: Array<{ id?: string; name: string } | string>;
  workspaceId?: string;
  created?: string;
  updated?: string;
  updatedBy?: string;
};

const defaultForm = (): LandingForm => ({
  status: 'active',
  countryId: 0,
  url: '',
  offers: 1,
});

export const useLandingDialog = (
  mode: LandingDialogMode,
  getData: () => LandingDialogData,
  emitClose: () => void,
) => {
  const store = useStore();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const tagsPlanLimits = usePlanLimits('tagsLandings');
  const edit = ref(mode === 'edit');
  const show = ref(false);
  const step = ref(1);
  const landingId = ref('');
  const disableWorkspaceSelect = ref(false);
  const emptyUrlSend = ref(false);
  const oldHash = ref('');
  const scrollPanel = ref<HTMLElement | null>(null);
  const scrollPanelWrapper = ref<HTMLElement | null>(null);

  const form = ref<LandingForm>(defaultForm());

  const reportForm = useReportForm(() => ({ edit: edit.value, mode: edit.value ? 'edit' : 'create' }));

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Landing Page Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of landing page')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Landing Page Settings')}</div><div class='introjs-tooltip__content'>${t('Enter the URL of landing page')}</div>`,
    4: `<div class='introjs-tooltip__title'>${t('Landing Page Settings')}</div><div class='introjs-tooltip__content'>${t('Create landing page element')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'landings',
  }));

  const tutorial = useTutorialApi();

  const {
    fieldErrors,
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const reportUtilsApi = useReportUtils({});

  const modalTitle = computed(() => {
    if (mode === 'edit') return t('Edit Landing');
    if (mode === 'duplicate') return t('Duplicate Landing');
    return t('Create Landing');
  });

  const disabledChips = ['+ {clickId}'];

  const chips = computed(() =>
    availableTokens
      .filter((item) => disabledChips.indexOf(item) === -1)
      .map((item) => ({
        tag: item,
        disabled: false,
      })),
  );

  const countryListPlusGlobal = computed(() => store.countryListPlusGlobal);

  const countryName = computed(() => {
    if (!form.value.countryId) return '';
    return countryListPlusGlobal.value?.find((item: { id: number }) => item.id === form.value.countryId)?.name;
  });

  const nameDescription = computed(() => {
    const countryLabel = countryName.value || t('Global');
    const nameLabel = form.value.name || t('None');
    return `${countryLabel} - ${nameLabel}`;
  });

  const workspacesList = computed(() => reportForm.workspacesList.value);

  const isActionsDisabled = computed(() => {
    if (mode !== 'edit') return false;
    return !!aclReadOnly({
      workspaceId: form.value.workspaceId,
      id: landingId.value,
      type: 'landings',
    });
  });

  const showPlanLimitsWarning_tags = computed(
    () => !!form.value.tags?.length && tagsPlanLimits.hasPlanLimitsRestriction('tagsLandings'),
  );

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const processForm = (source: LandingForm) => {
    const preparedForm = { ...source };
    preparedForm.tags = preparedForm?.tags?.map((item) =>
      typeof item === 'object' && item?.id ? item.name : item,
    ) as LandingForm['tags'];
    return preparedForm;
  };

  const patchEmptyUrlFieldError = () => {
    if (!emptyUrlSend.value || !fieldErrors.value.url) return;

    fieldErrors.value = {
      ...fieldErrors.value,
      url: [t('CUSTOM_VALIDATION_EMPTY_URL')],
    };

    const items = [{ message: t('Validation failed') }];
    Object.entries(fieldErrors.value).forEach(([field, messages]) => {
      messages.forEach((message) => {
        const hasFieldPrefix = message.includes('→');
        const prefixedMessage = hasFieldPrefix ? message : `${t(field)} → ${message}`;
        items.push({ message: prefixedMessage, type: field });
      });
    });
    errors.value = items;
    errorsControlActive.value = items.length > 0;
  };

  const applySubmitErrors = (error: unknown) => {
    const axiosError = error as { message?: string; response?: { status?: number } };
    if (
      axiosError.response?.status === 403
      || axiosError.response?.status === 400
      || axiosError.message === 'Network Error'
    ) {
      return;
    }
    if (axiosError.response?.status === 409 && showPlanLimitsErrorInToast(error)) {
      return;
    }
    if (!axiosError.response) {
      return;
    }

    applyApiErrors(error);
    patchEmptyUrlFieldError();
  };

  const loadLandingFromApi = async (id: string) => {
    const data = await getLandingApiRequest(id);
    const fields = mode === 'edit'
      ? ['countryId', 'name', 'offers', 'status', 'tags', 'url', 'workspaceId', 'created', 'updated', 'updatedBy']
      : ['countryId', 'name', 'offers', 'status', 'tags', 'url', 'workspaceId'];

    form.value = pick(data, fields) as LandingForm;
  };

  const loadCreateForm = () =>
    reportForm.loadForm({
      defaultForm,
      form: form.value,
    });

  const loadData = async () => {
    const data = getData();

    if (mode === 'edit') {
      edit.value = true;
      landingId.value = data.id || (store.getTableSelectedItem().id as string);
      store.setLoading(true);
      try {
        await loadLandingFromApi(landingId.value);
        show.value = true;
        oldHash.value = getCurrentHash();
      } finally {
        store.setLoading(false);
      }
      return;
    }

    if (mode === 'duplicate') {
      store.setLoading(true);
      try {
        const sourceId = store.getTableSelectedItem().id as string;
        await loadLandingFromApi(sourceId);
        landingId.value = '';
        form.value.name = `${form.value.name} (${t('____x.copy')})`;
        show.value = true;
        oldHash.value = getCurrentHash();
      } finally {
        store.setLoading(false);
      }
      return;
    }

    store.setLoading(true);
    try {
      await loadCreateForm();
      tutorial.form();
      oldHash.value = getCurrentHash();
      show.value = true;

      if (data.workspaceId) {
        form.value.workspaceId = data.workspaceId;
        disableWorkspaceSelect.value = true;
      }
    } finally {
      store.setLoading(false);
    }
  };

  const onDialogScroll = () => {
    $emitter.emit('dialog-scroll', 'landing-dialog');
  };

  const onDialogScrollThrottled = throttle(onDialogScroll, 10);

  const bindScrollListeners = (active: boolean) => {
    helpers.afterBrowserRedraw(() => {
      const panel = scrollPanel.value;
      const wrapper = scrollPanelWrapper.value;
      if (!panel) return;

      if (active) {
        panel.addEventListener('scroll', onDialogScrollThrottled);
        wrapper?.addEventListener('scroll', onDialogScrollThrottled);
      } else {
        panel.removeEventListener('scroll', onDialogScrollThrottled);
        wrapper?.removeEventListener('scroll', onDialogScrollThrottled);
      }
    });
  };

  watch(show, (value) => {
    bindScrollListeners(value);
  });

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const clearTags = () => {
    form.value.tags = [];
  };

  const submit = async () => {
    store.setLoading(true);
    emptyUrlSend.value = !form.value.url;
    clearErrors();

    try {
      if (mode === 'edit') {
        const data = await putUpdateLandingApiRequest(landingId.value, processForm(form.value));
        await refreshUserProfileFromApi();
        oldHash.value = getCurrentHash();
        reportUtilsApi.updateName('landings', data.id as string);
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Landing has been changed'));
        getData()?.onEdit?.(data);
        close();
        return;
      }

      const result = await postCreateLandingApiRequest(processForm(form.value));
      await refreshUserProfileFromApi();
      oldHash.value = getCurrentHash();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Landing has been saved'));
      close();
      getData()?.onClose?.(result);
    } catch (error) {
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
    }
  };

  onMounted(async () => {
    if (!store.cache.tags.landings.isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'landings' });
    }
    await loadData();
  });

  onUnmounted(() => {
    bindScrollListeners(false);
  });

  return {
    store,

    t,
    show,
    step,
    form,
    landingId,
    disableWorkspaceSelect,
    errors,
    errorsControlActive,
    errorsDict,
    modalTitle,
    chips,
    countryListPlusGlobal,
    nameDescription,
    workspacesList,
    isActionsDisabled,
    showPlanLimitsWarning_tags,
    scrollPanel,
    scrollPanelWrapper,
    tutorialIntro,
    tutorial,
    onChangeWorkspace: () => {},
    checkClose,
    clearTags,
    submit,
    getIntroParams: tutorialIntro.getIntroParams,
  };
};
